import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert, AuthCard, PasswordInput } from '../../components/ui.jsx';

// /team/join?token=… — an invited user activates their access: a 6-digit
// code to the invited email (proves the inbox), then a password.
export default function TeamJoin() {
  const { t, errorText } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState('start');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api(`/team-invite/${token}`).then(setInvite).catch((e) => setError(errorText(e))); }, [token, errorText]);

  async function sendCode() {
    setBusy(true); setError('');
    try { await api(`/team-invite/${token}/code`, { method: 'POST' }); setStep('code'); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }
  async function complete(e) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await api(`/team-invite/${token}/complete`, { method: 'POST', body: { code: code.trim(), password } });
      signIn(r.token, r.user);
      navigate('/account/workspace');
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }

  if (!invite) return <AuthCard title={t('teamJoin.title')}>{error ? <><Alert>{error}</Alert><p><Link to="/login">{t('teamJoin.toLogin')}</Link></p></> : <p className="muted">{t('common.loading')}</p>}</AuthCard>;
  return (
    <AuthCard title={t('teamJoin.title')} lead={t('teamJoin.lead', { company: invite.company, role: t(`team.role.${invite.role}`) })}>
      <p><strong>{invite.name}</strong>{invite.jobTitle ? ` — ${invite.jobTitle}` : ''}<br /><span className="muted">{invite.email}</span></p>
      <Alert>{error}</Alert>
      {step === 'start' ? (
        <>
          <p>{t('teamJoin.codeLead', { email: invite.email })}</p>
          <button type="button" className="btn btn-primary btn-block" onClick={sendCode} disabled={busy}>{t('teamJoin.sendCode')}</button>
        </>
      ) : (
        <form onSubmit={complete} className="auth-form">
          <label className="field"><span className="field-label">{t('teamJoin.code')}</span>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required /></label>
          <label className="field"><span className="field-label">{t('teamJoin.password')}</span>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required minLength={8} /></label>
          <p className="field-hint">{t('teamJoin.passwordHint')}</p>
          <button type="submit" className="btn btn-primary btn-block" disabled={busy || code.length !== 6 || password.length < 8}>{t('teamJoin.activate')}</button>
          <button type="button" className="btn btn-link btn-sm" onClick={sendCode} disabled={busy}>{t('teamJoin.resend')}</button>
        </form>
      )}
    </AuthCard>
  );
}
